import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { LegalDoc } from '../components/Legal.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  legalKicker: 'Legal',
  legalTitle: 'Privacy policy.',
  legalLede: 'What we collect, why we collect it, and how you can get it back or tell us to forget it. Written to be read, not skimmed past.',
  legalLastUpdated: 'Last updated: 14 April 2026',
  legalReadTime: '~6 min read',
  legalIntro: "We're SaySomethingIn Cyf., a small company in Bangor, Wales. This policy covers every site we run and the SSi learning app. We don't sell your data, we don't track you around the rest of the internet, and we collect as little as we can get away with while still running a working service.",
  legalContactNote: "Data-protection questions, access requests, or anything that isn't covered here – write to admin@saysomethingin.com and we'll reply personally. Our ICO registration number is ZA512341.",

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Document head', keys: ['legalKicker','legalTitle','legalLede','legalLastUpdated','legalReadTime','legalIntro','legalContactNote'], long:['legalLede','legalIntro','legalContactNote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const SECTIONS = [
  {
    id: 'what-we-collect',
    heading: 'What we collect',
    body: [
      "When you sign up, we collect your name, your email address, and (if you subscribe) the details needed to take payment – which we pass straight to Stripe. We don't store card numbers ourselves.",
      "When you use the app, we record which lessons you've started, how far through them you got, and the pause-and-speak timings that make the method work. That's what powers 'resume where you left off' and the weekly progress email, if you've opted in.",
      'We keep server logs of IP address, user agent, and request time for up to 30 days. We use them to diagnose crashes and keep the site up, nothing else.',
    ],
  },
  {
    id: 'why-we-collect-it',
    heading: 'Why we collect it',
    body: [
      'The short version: to run the service you signed up for and reply when you write to us. The longer version, in plain English:',
      [
        'Your name + email: so we can email you a password reset, a receipt, or a reply when you contact support.',
        "Payment details: so Stripe can charge your card. We see the last four digits and the expiry; that's it.",
        'Lesson progress: so the app remembers where you are and shows you the right next lesson.',
        "Server logs: so we can tell when something's broken before you have to.",
      ],
      "We don't use any of this to build an advertising profile. We don't have one to build.",
    ],
  },
  {
    id: 'what-we-dont-do',
    heading: "What we don't do",
    body: [
      "We don't sell, rent, or trade your data. We've never done it and we're not going to start.",
      "We don't embed third-party ad trackers, pixels, or 'analytics' that follow you across the web. We run Plausible for aggregate page-view counts – it's cookie-free and doesn't identify you.",
      "We don't use your voice recordings for anything other than the lesson you're doing. They're processed in the browser and not uploaded.",
    ],
  },
  {
    id: 'how-long-we-keep-it',
    heading: 'How long we keep it',
    body: [
      "Account data stays for as long as your account is open. If you cancel and don't come back, we delete it after 24 months – earlier if you ask.",
      'Payment records we keep for 7 years, because HMRC says we have to.',
      'Server logs roll off after 30 days. Email support threads stay in our helpdesk for 2 years so we have context if you write to us again.',
    ],
  },
  {
    id: 'your-rights',
    heading: 'Your rights',
    body: [
      "Under UK GDPR you can ask us to show you what we hold about you, correct anything that's wrong, or delete it entirely. Email admin@saysomethingin.com with 'Data request' in the subject and we'll have a reply with you within a working week – usually the same day.",
      "You can also complain to the Information Commissioner's Office at ico.org.uk if you think we've got something wrong. We'd rather you wrote to us first – we'll probably fix it faster.",
    ],
  },
  {
    id: 'cookies',
    heading: 'Cookies',
    body: [
      "We set one essential cookie that keeps you logged in, and one that remembers your preferred language. That's it. Neither of them is used for advertising.",
      "We don't use a cookie banner because we don't set any cookies that require consent. If that ever changes we'll put one up, not the other way round.",
    ],
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: [
      "If we change anything material – what we collect, who we share it with, how long we keep it – we'll email every active subscriber before the change takes effect. Typo fixes and formatting tweaks we'll just push.",
    ],
  },
];

export default function Privacy() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Privacy policy">
      <Header t={tweaks}/>
      <main>
        <LegalDoc t={tweaks} sections={SECTIONS}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
